import React, { useRef } from 'react';
import { useLocale } from '../i18n/LocaleContext';
import './BuildProgressTimeline.css';

function ActiveBeacon() {
  return (
    <span className="build-progress-beacon" aria-hidden="true">
      <span className="build-progress-beacon-ring" />
      <span className="build-progress-beacon-core" />
    </span>
  );
}

function truthfulActivityMessage(message, locale) {
  const value = String(message || '').trim();
  // Cloud UI builds delegate the real npm install to the isolated execution worker. The
  // control-plane `install_deps` step only prepares that dependency phase, so never show a
  // completed checkmark claiming installation already happened before the worker starts.
  if (value === 'Installing what your app needs...') {
    return locale === 'he' ? 'מכין את התלויות לבנייה המבודדת...' : 'Preparing dependencies for the isolated build...';
  }
  if (value === 'Building and testing your app in an isolated environment...') {
    return locale === 'he'
      ? 'מתקין תלויות, בונה ומפעיל את ה־Preview בסביבה המבודדת...'
      : 'Installing dependencies, building, and starting Preview in the isolated environment...';
  }
  return value;
}

/**
 * Customer-facing build status only. Polling, execution, recovery and Preview stay owned by
 * AgentChat; this component never invents timers or fake stages. Every visible work item comes
 * from the server-reported activity stream (plus the existing truthful initial fallback).
 */
export const VISIBLE_STEPS = 12;

/**
 * The visible list and the completed-step count. The count is taken from the whole activity
 * stream, not from the visible window: counting only the last 12 entries made the footer flip
 * "12 -> 11 -> 12" whenever the current message became a logged entry (incident 2026-10-16/17),
 * which looked like the build going backwards.
 */
export function timelineModel(entries = [], currentMessage = '', locale = 'he', preparingText = '') {
  const all = entries
    .map((entry, index) => ({
      id: entry.id || `${entry.at || 'progress'}-${index}`,
      message: truthfulActivityMessage(entry.message, locale),
      at: entry.at || null,
      operationId: entry.operationId || entry.jobId || null
    }))
    .filter(entry => entry.message)
    // Repeated polling/callback activity is not another completed build stage.
    // Collapse only adjacent duplicates for the same operation, preserving real transitions.
    .filter((entry, index, all) => index === 0
      || entry.message !== all[index - 1].message
      || entry.operationId !== all[index - 1].operationId);

  const truthfulCurrent = truthfulActivityMessage(currentMessage, locale);
  if (truthfulCurrent && all[all.length - 1]?.message !== truthfulCurrent) {
    all.push({ id: `current-${truthfulCurrent}`, message: truthfulCurrent, at: null });
  }
  if (!all.length) all.push({ id: 'preparing', message: preparingText, at: null });

  const visible = all.slice(-VISIBLE_STEPS);
  return { visible, completedCount: Math.max(0, all.length - 1), hiddenCount: all.length - visible.length };
}

export default function BuildProgressTimeline({ entries = [], currentMessage = '' }) {
  const { locale, t } = useLocale();
  const model = timelineModel(entries, currentMessage, locale, t('timeline.preparing'));
  const normalized = model.visible;
  // Never show the count going down within one build view.
  const maxCompletedRef = useRef(0);
  maxCompletedRef.current = Math.max(maxCompletedRef.current, model.completedCount);
  const completedCount = maxCompletedRef.current;
  const currentIndex = normalized.length - 1;

  return (
    <section className="build-progress-timeline" dir={locale === 'he' ? 'rtl' : 'ltr'} aria-label={t('timeline.label')} aria-live="polite" role="status">
      <header className="build-progress-heading">
        <div className="build-progress-brand-mark" aria-hidden="true">C</div>
        <div className="build-progress-heading-copy">
          <span className="build-progress-eyebrow">LIVE BUILD</span>
          <h2>{t('timeline.title')}</h2>
          <p>{t('timeline.description')}</p>
        </div>
        <span className="build-progress-live-pill"><span />LIVE</span>
      </header>

      <div className="build-progress-current-card">
        <ActiveBeacon />
        <div>
          <span className="build-progress-current-label">{locale === 'he' ? 'עובד עכשיו' : 'Working now'}</span>
          <strong>{normalized[currentIndex].message}</strong>
        </div>
      </div>

      {model.hiddenCount > 0 && (
        <p className="build-progress-earlier">{locale === 'he' ? `ועוד ${model.hiddenCount} שלבים קודמים שהושלמו` : `${model.hiddenCount} earlier steps completed`}</p>
      )}
      <ol className="build-progress-steps">
        {normalized.map((entry, index) => {
          const isCurrent = index === currentIndex;
          return (
            <li key={entry.id} className={isCurrent ? 'is-current' : 'is-complete'}>
              <span className="build-progress-marker">
                {isCurrent ? <ActiveBeacon /> : <span className="build-progress-complete" aria-hidden="true">✓</span>}
              </span>
              <span className="build-progress-message">{entry.message}</span>
            </li>
          );
        })}
      </ol>

      <footer className="build-progress-footer">
        <span>{locale === 'he' ? `${completedCount} שלבים הושלמו` : `${completedCount} steps completed`}</span>
        <span>{locale === 'he' ? 'ה־Preview ייפתח אוטומטית כשהבנייה מוכנה' : 'Preview opens automatically when the build is ready'}</span>
      </footer>
    </section>
  );
}
